import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { saveEmergencyServiceBidding,getUserProfile } from '@/api/emergencyServiceApi';
import styles from './OrderDialog.module.scss';
import Membership from '@/components/Membership';
import { setUserInfo } from '@/store/slices/userSlice';
import { formatAppointmentTime } from '@/utils/utils';
import { useTranslation } from 'react-i18next';

export type OrderDialogMode = 'bidding' | 'fixed';

export type OrderDialogOrder = {
    imageUrl: string;
    title: string;
    distanceMiles: number | string;
    // dateLabel: string; // e.g., 'August 16th'
    // timeRangeLabel: string; // e.g., '11:15-11:45'
    appointment_time: string;
    address: string;
    mechanism: string; // e.g., 'Optimal Price'
    problemDescription: string;
    price: number; // in dollars
    biddingMechanism?: number; // 1: Fixed Price, 2: Bidding
    emergency_service_id?: string; // 紧急服务订单ID
};

export type OrderDialogProps = {
    open: boolean;
    mode: OrderDialogMode;
    order: OrderDialogOrder;
    defaultBid?: number;
    onConfirm: (payload: { mode: OrderDialogMode; bid?: number }) => void;
    onCancel: () => void;
};

const OrderDialog: React.FC<OrderDialogProps> = ({ open, mode, order, defaultBid, onConfirm, onCancel }) => {
    const [bid, setBid] = useState<number | undefined>(defaultBid);
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    // 获取用户信息和位置信息
    const userInfo = useAppSelector((state) => state.user.userInfo);
    const latitude = useAppSelector((state) => state.user.latitude);
    const longitude = useAppSelector((state) => state.user.longitude);
  //国际化
  const { t, i18n } = useTranslation();
    // 是否是会员
    const [isMember, setIsMember] = useState(false);

    // 添加会员弹窗状态控制
    const [membershipModalOpen, setMembershipModalOpen] = useState<boolean>(false);

    // 当打开或订单/默认值/模式变化时，重置出价
    useEffect(() => {
        if (open) {
            if (mode === 'bidding') {
                const fallback = order?.price ?? 0;
                const next = typeof defaultBid === 'number' ? defaultBid : fallback;
                setBid(next);
            } else {
                setBid(undefined);
            }
        }
    }, [open, defaultBid, order, mode]);

    const titleText = useMemo(() => {
        return mode === 'bidding' ? t('membership.BiddingOrder') : t('membership.OrderConfirmation');
    }, [mode]);

    // 处理会员购买弹窗的回调函数
    const handleMembershipCancel = () => {
        setMembershipModalOpen(false);
    };

    const handleMembershipConfirm = (values: any) => {
        console.log('Membership purchase confirmed:', values);
        
        // 检查是否是支付成功
        if (values.paymentSuccess) {
            message.success(t('membership.Membershipsuccessfully'));
            setMembershipModalOpen(false);
            setIsMember(true);

            // 支付成功后，更新 localStorage 中的 userinfo
            const userInfoStr = localStorage.getItem('userInfo');
            let userInfo = userInfoStr ? JSON.parse(userInfoStr) : {};
            //把购买的会员标识手动处理
            userInfo.emergency_service_member = true;

            dispatch(setUserInfo(userInfo));
            
            // 支付成功后，继续执行原来的操作
            handleOk(true); // 跳过会员检查
        } else {
            setMembershipModalOpen(false);
        }
    };

    const handleOk = async (skipMembershipCheck: boolean = false) => {
        // 如果不是跳过会员检查，先检查是否是会员
        if (!skipMembershipCheck) {
            // 检查是否是会员
            if (!isMember) {
                setMembershipModalOpen(true);
                return;
            }
        }
        // 先获取用户信息并打印
        try {
            const userProfileRes = await getUserProfile();
            console.log('getUserProfile response:', userProfileRes);
        } catch (error) {
            console.error('Failed to get user profile:', error);
        }

        if (mode === 'bidding') {
            // 如果bid为undefined，使用默认值
            const fallback = order?.price ?? 0;
            const finalBid = (typeof bid === 'number' && !Number.isNaN(bid)) ? bid : fallback;
            
            // 验证出价不能超过预算上限
            if (finalBid > order.price) {
                message.error(`${t('membership.Thelimit')} $${order.price}, ${t('membership.pleasetryagain')}`);
                return;
            }
            // 验证出价不能为0
            if (finalBid <= 0) {
                message.error(t('membership.Theamount'));
                return;
            }
            // 用户信息获取失败
            if (!userInfo?.user_id) {
                message.error(t('membership.Failedagain'));
                return;
            }
            // 位置信息获取失败
            if (!latitude || !longitude) {
                message.error(t('membership.Failedpermission'));
                return;
            }

            setLoading(true);
            try {
                // 调用竞价接口
                const params = {
                    emergency_service_id: order.emergency_service_id,
                    bidding_person_id: userInfo.user_id,
                    bidding: finalBid,
                    latitude: latitude,
                    longitude: longitude,
                    is_delete: null,
                };

                const res = await saveEmergencyServiceBidding(params);
                
                if (res && res.code === 200) {
                    message.success(res.data);
                    onConfirm({ mode, bid: finalBid });
                } else {
                    message.error(res?.message);
                }
            } catch (error: any) {
                // 检查是否是会员相关的错误
                if (error?.response?.status === 500 && 
                    error?.response?.data?.message === 'Vip_info not found or not authenticated') {
                    setMembershipModalOpen(true);
                } 
    
            } finally {
                setLoading(false);
            }
        } else {
            // Fixed Price 模式 status=1
            // 用户信息获取失败
            if (!userInfo?.user_id) {
                message.error(t('membership.Failedagain'));
                return;
            }
            // 位置信息获取失败
            if (!latitude || !longitude) {
                message.error(t('membership.Failedpermission'));
                return;
            }

            setLoading(true);
            try {
                // 一口价模式
                const params = {
                    emergency_service_id: order.emergency_service_id,
                    bidding_person_id: userInfo.user_id,
                    // bidding_id: userInfo.user_id,
                    bidding: order.price,
                    latitude: latitude,
                    longitude: longitude,
                    status: 1,
                    is_delete: null
                };

                const res = await saveEmergencyServiceBidding(params);
                if (res && res.code === 200) {
                    message.success(t('membership.Ordersuccessfully'));
                    onConfirm({ mode });
                } 
            } catch (error: any) {
                // 检查是否是会员相关的错误
                if (error?.response?.status === 500 && 
                    error?.response?.data?.message === 'Vip_info not found or not authenticated') {
                    setMembershipModalOpen(true);
                } 
            } finally {
                setLoading(false);
            }
        }
    };

    // 检查会员状态
    useEffect(() => {
        // 判断是否是会员
        if(userInfo?.vip_info?.length){
            setIsMember(true);
        }
    },[userInfo]);

    return (
        <>
            <Modal
                open={open}
                onCancel={onCancel}
                onOk={() => handleOk()}
                footer={null}
                closable={false}
                width={720}
                className={styles.orderDialog}
            >
            <div className={styles.container}>
                <div className={styles.header}>
                    <h3 className={styles.dialogTitle}>{titleText}</h3>
                    <div className={styles.rightDatetime}>
                        <span className={styles.date}>{formatAppointmentTime(order.appointment_time)}</span>
                    </div>
                    <button className={styles.closeBtn} onClick={onCancel} aria-label="Close" />
                </div>

                <div className={styles.summaryRow}>
                <img className={styles.thumb} src={order.imageUrl} alt={order.title} />
                    <div className={styles.summaryText}>
                        <div className={styles.titleLine}>
                            <div className={styles.orderTitle}>{order.title}</div>
                        </div>
                        <div className={styles.distanceRow}>
                            <div className={styles.distance}><span className={styles.distanceIcon} />{typeof order.distanceMiles === 'number' ? `${order.distanceMiles} ${t('searchList.MilesFromYou')}` : order.distanceMiles}</div>
                        </div>
                        <div className={styles.infoLine}><span className={styles.label}>{t('searchList.Address')}:</span>
                        <span className={styles.value}>
                        {/* {order.address} */}
                        {order?.address ? (order.address.slice(0, 15) + '****') : `${t('searchList.Address')} N/A`}
                        </span>
                        </div>
                        <div className={styles.infoLine}><span className={styles.label}>{t('membership.BiddingMechanism')}:</span><span className={styles.value}>{order.mechanism}</span></div>
                        <div className={styles.infoLine}><span className={styles.label}>{t('searchList.ProblemDescription')}:</span><span className={styles.value}>{order.problemDescription}</span></div>
                    </div>
                </div>

                <div className={styles.separator} />

                <div className={styles.priceRow}>
                    <span className={styles.priceLabel}>{mode === 'bidding' ? t('membership.Budget') : t('searchList.Price')}:</span>
                    <span className={styles.currency}>$</span>
                    <span className={styles.price}>{order.price}</span>
                </div>

                {mode === 'bidding' && (
                    <div className={styles.bidRow}>
                        <label className={styles.bidLabel} htmlFor="bid-input">{t('searchList.Bidding')}($):</label>
                        <Input
                            id="bid-input"
                            className={styles.bidInput}
                            value={bid as any}
                            inputMode="decimal"
                            onChange={(e) => {
                                const numeric = Number(e.target.value.replace(/[^0-9.]/g, ''));
                                if (Number.isNaN(numeric)) {
                                    setBid(undefined);
                                    return;
                                }
                                
                                // 验证不能超过预算上限
                                if (numeric > order.price) {
                                    setBid(order.price);
                                    message.warning(`${'membership.Thelimit'} $${order.price}`);
                                    return;
                                }
                                
                                setBid(numeric);
                            }}
                            onBlur={() => { /* 无下限，有上限（预算） */ }}
                            placeholder={mode === 'bidding' ? `${t('membership.Enteryourbid')} (≤ $${order.price})` : `${t('membership.Enteryourbid')}`}
                        />
                    </div>
                )}

                <div className={styles.footer}>
                    <button className={styles.cancelBtn} onClick={onCancel} disabled={loading}>{t('membership.Cancel')}</button>
                    <button className={styles.confirmBtn} onClick={() => handleOk()} disabled={loading}>
                        {loading ? `${t('membership.Submitting')}...` : t('membership.Confirm')}
                    </button>
                </div>
            </div>
            </Modal>

            <Membership 
                open={membershipModalOpen}
                onCancel={handleMembershipCancel}
                onConfirm={handleMembershipConfirm}
                membershipType={0}
            />
        </>
    );
};

export default OrderDialog;

